import { findClosestMatch } from './search-utils';

type ColumnMapping = { value?: string | null };

/**
 * Suggests the property a CSV column maps to: an exact alias of the header first, then the
 * closest fuzzy match among the property keys.
 *
 * @param header - The CSV column header.
 * @param propertyKeys - The keys of the properties a column can map to.
 * @param headerAliases - Lowercase header → property key, for headers the fuzzy match gets wrong.
 *   Default `{}`.
 * @returns The suggested property key, or `null` when nothing matches.
 */
export const suggestProperty = (
  header: string,
  propertyKeys: string[],
  headerAliases: Record<string, string> = {},
): string | null => {
  const key = header.trim().toLowerCase();
  // Own keys only: a header such as "constructor" must not read Object.prototype.
  return Object.prototype.hasOwnProperty.call(headerAliases, key)
    ? headerAliases[key]!
    : findClosestMatch(header, propertyKeys);
};

/**
 * Lists the required properties that no column is mapped to.
 *
 * @param columnMappings - The mapping of each CSV column, keyed by header.
 * @param requiredFields - The property keys that must be mapped.
 * @returns The required property keys still unmapped, in `requiredFields` order.
 */
export const getUnmappedRequiredFields = (
  columnMappings: Record<string, ColumnMapping>,
  requiredFields: string[],
): string[] => {
  const mapped = new Set(Object.values(columnMappings).map(({ value }) => value));
  return requiredFields.filter(field => !mapped.has(field));
};
